import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DeckGL from "@deck.gl/react";
import { FlyToInterpolator } from "@deck.gl/core";
import { Map } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";

import { renderLayers } from "./Layers";
import { callClaude } from "./agent/claude-client.js";
import { ConversationStore } from "./agent/conversation-store.js";
import { runAgent } from "./agent/runtime.js";
import { composeSystemPrompt } from "./agent/system-prompt.js";
import { DatasetStore } from "./data/dataset-store.js";
import { createPortwatchToolRegistry } from "./tools/register-tools.js";
import { buildServerTools } from "./tools/server-tools.js";
import { fetchSpilloverNetwork } from "./tools/portwatch-client.js";
import Header from "./components/Header.jsx";
import Sidebar from "./components/Sidebar.jsx";
import PortDetail from "./components/PortDetail.jsx";
import ChatPanel from "./components/ChatPanel.jsx";
import ApiSettings from "./components/ApiSettings.jsx";
import { formatCompact, formatNumber } from "./utils/format.js";
import { SIGNAL_LABEL } from "./utils/labels.js";

import "./index.css";

// 世界全体が見える初期ビュー（DESIGN.md §5）。
const INITIAL_VIEW_STATE = {
  longitude: 10,
  latitude: 25,
  zoom: 1.6,
  bearing: 0,
  pitch: 0,
};

const SIGNAL_HEX = { ok: "#34d27b", warn: "#e5a816", danger: "#e54d4d" };

// MapTiler のベクタースタイル（ダーク）。キーはオリジン制限つきのクライアントキー。
// 必要なら VITE_MAPTILER_STYLE_URL で差し替え可能。
const MAPTILER_STYLE_URL =
  import.meta.env.VITE_MAPTILER_STYLE_URL ||
  "https://api.maptiler.com/maps/37b72abb-a824-41ba-bc73-2709ca5db30c/style.json?key=XTDrgAG3Y8GOpjSthqnK";

// エージェント関連はモジュールスコープで一度だけ生成（再レンダーで作り直さない）。
const datasetStore = new DatasetStore();
const conversationStore = new ConversationStore();
const toolRegistry = createPortwatchToolRegistry(datasetStore);
const SYSTEM_PROMPT = composeSystemPrompt();

const MAX_LOG_ENTRIES = 300;
const API_KEY_STORAGE = "portwatch-agent.apiKey";
const MODEL_STORAGE = "portwatch-agent.model";
const MAX_TOKENS_STORAGE = "portwatch-agent.maxTokens";
const TOOL_WEB_SEARCH_STORAGE = "portwatch-agent.tools.webSearch";
const SIDEBAR_OPEN_STORAGE = "portwatch-agent.ui.sidebar";
const CHAT_OPEN_STORAGE = "portwatch-agent.ui.chat";
const LEGEND_OPEN_STORAGE = "portwatch-agent.ui.legend";
const CHAT_WIDTH_STORAGE = "portwatch-agent.ui.chatWidth";
const DEFAULT_CHAT_WIDTH = 360;
const MIN_CHAT_WIDTH = 300;
const MAX_CHAT_WIDTH = 720;
const DEFAULT_MODEL = "claude-sonnet-4-6";
const DEFAULT_MAX_TOKENS = 16000;

const WELCOME_MESSAGE = {
  id: "welcome",
  role: "assistant",
  content:
    "港湾・海上要衝について質問してください。地点の検索→時系列取得→分析を順に実行します。地図上のマーカーやアラートをクリックして「AI に分析させる」こともできます。",
};

function clampChatWidth(width) {
  return Math.min(MAX_CHAT_WIDTH, Math.max(MIN_CHAT_WIDTH, width));
}

function loadSetting(key, fallback = "") {
  try {
    return globalThis.localStorage?.getItem(key) ?? fallback;
  } catch {
    return fallback;
  }
}
function saveSetting(key, value) {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // 保存失敗はメモリ保持のみで継続。
  }
}
function removeSetting(key) {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    // 削除失敗は無視。
  }
}

// 永続化された Anthropic 形式履歴から、表示用チャットバブルを復元する。
function toDisplayMessages(apiMessages) {
  const bubbles = [];
  apiMessages.forEach((message, index) => {
    if (message.role === "user" && typeof message.content === "string") {
      bubbles.push({ id: `restored-${index}`, role: "user", content: message.content });
      return;
    }
    if (message.role === "assistant" && Array.isArray(message.content)) {
      const text = message.content
        .filter((block) => block.type === "text")
        .map((block) => block.text)
        .join("\n")
        .trim();
      if (text) {
        const isProgress = message.content.some((b) => b.type === "tool_use");
        bubbles.push({
          id: `restored-${index}`,
          role: "assistant",
          content: text,
          ...(isProgress ? { kind: "progress" } : {}),
        });
      }
    }
  });
  return bubbles;
}

function initialChatMessages() {
  return [WELCOME_MESSAGE, ...toDisplayMessages(conversationStore.getMessages())];
}

function describeEvent(event) {
  switch (event.type) {
    case "model_request":
      return `Claudeへ第${event.iteration}回のリクエストを送信`;
    case "model_response":
      return `Claude応答: ${event.stopReason}`;
    case "tool_start":
      return `ツール開始: ${event.name}`;
    case "tool_success":
      return `ツール完了: ${event.name}`;
    case "tool_error":
      return `ツール失敗: ${event.name} - ${event.message}`;
    default:
      return event.type;
  }
}

// ホバー時のツールチップ HTML を組み立てる。
function getTooltip({ object }) {
  if (!object) return null;

  // 波及アーク（影響先の港）。
  if (object.toPortname && Array.isArray(object.to)) {
    return {
      html: `<div style="font-weight:600;margin-bottom:2px">${object.toPortname}${
        object.country ? ` · ${object.country}` : ""
      }</div>
        <div style="font-family:monospace;font-size:11px">輸送能力リスク ${formatCompact(
          object.capacityAtRisk,
        )} t/日（推計）</div>
        <div style="font-family:monospace;font-size:11px;opacity:0.8">平均通過 ${
          object.transitDays ?? "—"
        } 日</div>`,
      style: {
        background: "rgba(19,26,43,0.94)",
        color: "#e1e7ef",
        border: "1px solid #2a3d54",
        borderRadius: "6px",
        padding: "8px 10px",
        fontSize: "13px",
      },
    };
  }

  const isPort = object.kind === "port";
  const main = isPort
    ? `入港数 ${formatNumber(object.latest?.portcalls)}`
    : `通航数 ${formatNumber(object.latest?.n_total)}`;
  const sub = isPort
    ? `輸出入 ${formatCompact(object.latest?.import)} / ${formatCompact(
        object.latest?.export,
      )} t（推計）`
    : `通過貿易量 ${formatCompact(object.latest?.capacity)} t（推計）`;
  const color = SIGNAL_HEX[object.signal] ?? SIGNAL_HEX.ok;

  return {
    html: `<div style="font-weight:600;margin-bottom:2px">
        <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px"></span>
        ${object.fullname || object.portname}
      </div>
      <div style="font-family:monospace;font-size:11px">${main}</div>
      <div style="font-family:monospace;font-size:11px;opacity:0.8">${sub}</div>`,
    style: {
      background: "rgba(19,26,43,0.94)",
      color: "#e1e7ef",
      border: "1px solid #2a3d54",
      borderRadius: "6px",
      padding: "8px 10px",
      fontSize: "13px",
    },
  };
}

function App() {
  // --- 監視データ・地図 ---
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [viewState, setViewState] = useState(INITIAL_VIEW_STATE);
  // 波及ネットワーク（選択港 from → 影響先港 to のアーク）。
  const [network, setNetwork] = useState(null); // { portid, portname, from, arcs } | null
  const [networkLoading, setNetworkLoading] = useState(false);
  // クリックで詳細が開く地点（港/チョークポイント）にホバー中かどうか＝カーソル形状の切替用。
  const [hoverClickable, setHoverClickable] = useState(false);

  // --- エージェント・設定 ---
  const [messages, setMessages] = useState(initialChatMessages);
  const [logs, setLogs] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [apiKey, setApiKey] = useState(() => loadSetting(API_KEY_STORAGE));
  const [model, setModel] = useState(
    () => loadSetting(MODEL_STORAGE) || DEFAULT_MODEL,
  );
  const [maxTokens, setMaxTokens] = useState(
    () =>
      Number(loadSetting(MAX_TOKENS_STORAGE, String(DEFAULT_MAX_TOKENS))) ||
      DEFAULT_MAX_TOKENS,
  );
  // サーバー側ツール（web検索）は別途課金のため既定オフ（オプトイン）。
  const [toolWebSearch, setToolWebSearch] = useState(
    () => loadSetting(TOOL_WEB_SEARCH_STORAGE) === "true",
  );
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  // パネルの開閉（地図を広く使うため。localStorage に保存して次回も維持）。
  const [sidebarOpen, setSidebarOpen] = useState(
    () => loadSetting(SIDEBAR_OPEN_STORAGE, "false") === "true",
  );
  const [chatOpen, setChatOpen] = useState(
    () => loadSetting(CHAT_OPEN_STORAGE, "false") === "true",
  );
  // AI エージェント UI は API キーが設定されるまで出さない（タブもパネルも非表示）。
  const chatVisible = chatOpen && Boolean(apiKey);
  const [legendOpen, setLegendOpen] = useState(
    () => loadSetting(LEGEND_OPEN_STORAGE, "false") === "true",
  );
  // チャットパネル幅（ドラッグでリサイズ、確定時のみ保存）。
  const [chatWidth, setChatWidth] = useState(() =>
    clampChatWidth(
      Number(loadSetting(CHAT_WIDTH_STORAGE, String(DEFAULT_CHAT_WIDTH))) ||
        DEFAULT_CHAT_WIDTH,
    ),
  );
  const abortControllerRef = useRef(null);

  const toggleSidebar = (open) =>
    setSidebarOpen((current) => {
      const next = open ?? !current;
      saveSetting(SIDEBAR_OPEN_STORAGE, String(next));
      return next;
    });
  const toggleChat = (open) =>
    setChatOpen((current) => {
      const next = open ?? !current;
      saveSetting(CHAT_OPEN_STORAGE, String(next));
      return next;
    });
  const toggleLegend = (open) =>
    setLegendOpen((current) => {
      const next = open ?? !current;
      saveSetting(LEGEND_OPEN_STORAGE, String(next));
      return next;
    });
  const handleChatWidthChange = useCallback((width, { commit = false } = {}) => {
    const next = clampChatWidth(width);
    setChatWidth(next);
    if (commit) saveSetting(CHAT_WIDTH_STORAGE, String(next));
  }, []);

  // 起動時に監視用集約 JSON をロードする。
  useEffect(() => {
    const controller = new AbortController();
    fetch("./data/portwatch.json", { signal: controller.signal })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((json) => setData(json))
      .catch((err) => {
        if (err.name !== "AbortError") setLoadError(err.message);
      });
    return () => controller.abort();
  }, []);

  const allLocations = useMemo(
    () => [...(data?.chokepoints ?? []), ...(data?.ports ?? [])],
    [data],
  );

  const selected = useMemo(
    () => allLocations.find((d) => d.portid === selectedId) ?? null,
    [allLocations, selectedId],
  );

  const flyTo = useCallback((loc) => {
    if (!loc || loc.lon == null) return;
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    setViewState((prev) => ({
      ...prev,
      longitude: loc.lon,
      latitude: loc.lat,
      zoom: Math.max(prev.zoom, 5),
      transitionDuration: reduceMotion ? 0 : 1200,
      transitionInterpolator: reduceMotion ? undefined : new FlyToInterpolator(),
    }));
  }, []);

  const selectLocation = useCallback(
    (portid) => {
      setSelectedId(portid);
      flyTo(allLocations.find((d) => d.portid === portid));
    },
    [allLocations, flyTo],
  );

  const handleClick = useCallback((object) => {
    if (object?.portid) setSelectedId(object.portid);
  }, []);

  // 波及ネットワークの表示/非表示トグル（港のみ）。
  const handleShowNetwork = useCallback(
    async (location) => {
      if (!location || location.kind !== "port") return;
      if (network?.portid === location.portid) {
        setNetwork(null);
        return;
      }
      setNetworkLoading(true);
      try {
        const result = await fetchSpilloverNetwork({ portid: location.portid });
        setNetwork({
          portid: location.portid,
          portname: location.fullname || location.portname,
          from: result.from ?? [location.lon, location.lat],
          arcs: result.arcs,
        });
      } catch {
        setNetwork(null);
      } finally {
        setNetworkLoading(false);
      }
    },
    [network],
  );

  const layers = useMemo(
    () => renderLayers(data, { onClick: handleClick, selectedId, network }),
    [data, handleClick, selectedId, network],
  );

  // --- 設定 ---
  const handleSaveSettings = () => {
    saveSetting(API_KEY_STORAGE, apiKey);
    saveSetting(MODEL_STORAGE, model);
    saveSetting(MAX_TOKENS_STORAGE, String(maxTokens));
    saveSetting(TOOL_WEB_SEARCH_STORAGE, String(toolWebSearch));
    setIsSettingsOpen(false);
  };
  const handleDeleteKey = () => {
    setApiKey("");
    removeSetting(API_KEY_STORAGE);
  };

  // --- エージェント ---
  const handleAbort = () => abortControllerRef.current?.abort();

  const handleReset = () => {
    if (isRunning) return;
    conversationStore.clear();
    datasetStore.clear();
    setMessages([WELCOME_MESSAGE]);
    setLogs([]);
  };

  const runInstruction = useCallback(
    async (content) => {
      if (isRunning) return;
      setMessages((current) => [
        ...current,
        { id: crypto.randomUUID(), role: "user", content },
      ]);

      if (!apiKey) {
        setIsSettingsOpen(true);
        setMessages((current) => [
          ...current,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            kind: "notice",
            content:
              "Claude APIキーが未設定です。右上の「API設定」で入力してください（platform.claude.com で取得）。",
          },
        ]);
        return;
      }

      setIsRunning(true);
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const result = await runAgent({
          instruction: content,
          messages: conversationStore.getMessages(),
          toolRegistry,
          system: SYSTEM_PROMPT,
          signal: controller.signal,
          callModel: (request) =>
            callClaude({
              ...request,
              tools: [
                ...request.tools,
                ...buildServerTools({ webSearch: toolWebSearch }),
              ],
              apiKey,
              model,
              maxTokens,
            }),
          onEvent: (event) => {
            if (event.type === "assistant_text") {
              setMessages((current) => [
                ...current,
                {
                  id: crypto.randomUUID(),
                  role: "assistant",
                  kind: "progress",
                  content: event.text,
                },
              ]);
              return;
            }
            setLogs((current) =>
              [
                ...current,
                { id: crypto.randomUUID(), message: describeEvent(event) },
              ].slice(-MAX_LOG_ENTRIES),
            );
          },
        });

        conversationStore.setMessages(result.messages);

        const pushAssistant = (extra) =>
          setMessages((current) => [
            ...current,
            { id: crypto.randomUUID(), role: "assistant", ...extra },
          ]);

        if (result.status === "iteration_limit") {
          pushAssistant({
            kind: "notice",
            content:
              "⚠️ 反復上限に達したため処理を途中で停止しました。続けて送信すると取得済みの情報から再開できます。対象や期間を絞ると確実です。",
          });
          if (result.content) pushAssistant({ content: result.content });
        } else {
          const response =
            result.content ||
            (result.status === "aborted"
              ? "処理を中断しました。"
              : `処理を完了できませんでした（状態: ${result.status}）。`);
          pushAssistant({ content: response });
        }
      } catch (error) {
        const aborted = error?.name === "AbortError";
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        setMessages((current) => [
          ...current,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            content: aborted ? "処理を中断しました。" : `エラー: ${errorMessage}`,
          },
        ]);
      } finally {
        abortControllerRef.current = null;
        setIsRunning(false);
      }
    },
    [apiKey, isRunning, maxTokens, model, toolWebSearch],
  );

  // 地図詳細の「AI に分析させる」→ portid を含めた依頼を送る。
  const handleAnalyzeLocation = useCallback(
    (loc) => {
      const kindLabel = loc.kind === "chokepoint" ? "チョークポイント" : "港";
      runInstruction(
        `${loc.fullname || loc.portname}（${kindLabel}, portid=${loc.portid}, scope=${loc.kind}）の直近6ヶ月の取扱量トレンドと異常を分析してください。`,
      );
    },
    [runInstruction],
  );

  return (
    <div
      className={`app-shell${sidebarOpen ? " sidebar-open" : ""}${
        chatVisible ? " chat-open" : ""
      }`}
      style={{ "--chat-width": `${chatWidth}px` }}
    >
      <div className="map-root">
        <DeckGL
          viewState={viewState}
          onViewStateChange={({ viewState: vs }) => setViewState(vs)}
          controller={true}
          layers={layers}
          getTooltip={getTooltip}
          onHover={(info) =>
            setHoverClickable(
              Boolean(info.object) &&
                (info.layer?.id === "ports" ||
                  info.layer?.id === "chokepoints"),
            )
          }
          getCursor={({ isDragging }) =>
            isDragging ? "grabbing" : hoverClickable ? "pointer" : "grab"
          }
        >
          <Map mapStyle={MAPTILER_STYLE_URL} attributionControl={false} reuseMaps />
        </DeckGL>
      </div>

      <Header
        counts={data?.counts}
        latestDate={data?.latestDate}
        lastUpdated={data?.lastUpdated}
      >
        <ApiSettings
          apiKey={apiKey}
          model={model}
          maxTokens={maxTokens}
          toolWebSearch={toolWebSearch}
          isOpen={isSettingsOpen}
          onToggle={() => setIsSettingsOpen((v) => !v)}
          onApiKeyChange={setApiKey}
          onModelChange={setModel}
          onMaxTokensChange={setMaxTokens}
          onToolWebSearchChange={setToolWebSearch}
          onSave={handleSaveSettings}
          onDeleteKey={handleDeleteKey}
        />
      </Header>

      {sidebarOpen ? (
        <Sidebar
          counts={data?.counts}
          anomalies={data?.anomalies}
          onSelect={selectLocation}
          onClose={() => toggleSidebar(false)}
        />
      ) : (
        <button
          type="button"
          className="panel-reopen panel-reopen-left"
          onClick={() => toggleSidebar(true)}
          title="監視パネルを開く"
        >
          監視 »
        </button>
      )}

      {selected && (
        <PortDetail
          location={selected}
          onClose={() => setSelectedId(null)}
          onAnalyze={apiKey ? handleAnalyzeLocation : undefined}
          onShowNetwork={handleShowNetwork}
          networkActive={network?.portid === selected.portid}
          networkLoading={networkLoading}
        />
      )}

      {network && (
        <div className="network-flag">
          <span className="dot" />
          波及ネットワーク: {network.portname}（{network.arcs.length}件）
          <button
            type="button"
            className="network-flag-close"
            onClick={() => setNetwork(null)}
            aria-label="ネットワークを消す"
          >
            ×
          </button>
        </div>
      )}

      {chatVisible ? (
        <ChatPanel
          messages={messages}
          logs={logs}
          isRunning={isRunning}
          hasApiKey={Boolean(apiKey)}
          onSubmit={runInstruction}
          onAbort={handleAbort}
          onReset={handleReset}
          onOpenSettings={() => setIsSettingsOpen(true)}
          onClose={() => toggleChat(false)}
          onWidthChange={handleChatWidthChange}
        />
      ) : apiKey ? (
        <button
          type="button"
          className="panel-reopen panel-reopen-right"
          onClick={() => toggleChat(true)}
          title="AIエージェントを開く"
        >
          « AI
        </button>
      ) : null}

      {legendOpen ? (
        <div className={`legend${chatVisible ? "" : " legend-wide"}`}>
          <div className="legend-head">
            <div className="legend-items">
              <span className="legend-row">
                <span className="dot" style={{ background: SIGNAL_HEX.ok }} />{" "}
                {SIGNAL_LABEL.ok}
              </span>
              <span className="legend-row">
                <span className="dot" style={{ background: SIGNAL_HEX.warn }} />{" "}
                {SIGNAL_LABEL.warn}
              </span>
              <span className="legend-row">
                <span className="dot" style={{ background: SIGNAL_HEX.danger }} />{" "}
                {SIGNAL_LABEL.danger}
              </span>
            </div>
            <button
              type="button"
              className="legend-toggle"
              onClick={() => toggleLegend(false)}
              title="凡例を閉じる"
              aria-label="凡例を閉じる"
            >
              ×
            </button>
          </div>
          <p className="legend-note">
            マーカー径＝取扱量。リング＝チョークポイント。数値は推計。
          </p>
        </div>
      ) : (
        <button
          type="button"
          className={`legend legend-reopen${chatVisible ? "" : " legend-wide"}`}
          onClick={() => toggleLegend(true)}
          title="凡例を開く"
          aria-label="凡例を開く"
          aria-expanded="false"
        >
          凡例
        </button>
      )}

      <div className="attribution">
        <a
          href="https://www.maptiler.com/copyright/"
          target="_blank"
          rel="noopener noreferrer"
        >
          © MapTiler
        </a>{" "}
        ·{" "}
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noopener noreferrer"
        >
          © OpenStreetMap contributors
        </a>{" "}
        · IMF PortWatch
      </div>

      {loadError && (
        <div className="attribution" style={{ bottom: "auto", top: 60, right: 16 }}>
          データ読み込みエラー: {loadError}
        </div>
      )}
    </div>
  );
}

export default App;
